/**
 * «Telegram»: сотрудник сам привязывает свой Telegram к учётке.
 *
 * Отдельный лендинг без меню кабинета: сюда ведёт бот людей, которые кабинетом
 * не пользуются, — лишние разделы только уводили бы от кнопки.
 *
 * В CRM `sUser.tgID` есть не у всех. Кнопка выдаёт одноразовую ссылку на бота
 * (15 минут); Start по ней — и бот знает, кто это. Пока ссылка открыта,
 * экран опрашивает сервис и сам покажет «Подключено».
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { mskTime } from "@/lib/dates";
import { loadTgLink, newTgLink, type TgLinkUrl } from "@/lib/tg";

export function TelegramPage() {
  const [pending, setPending] = useState<TgLinkUrl | null>(null);
  const [failure, setFailure] = useState("");
  const waiting = pending !== null && new Date(pending.expire) > new Date();
  const link = useQuery({
    queryKey: ["tg-link"],
    queryFn: loadTgLink,
    refetchInterval: (q) => (waiting && !q.state.data?.linked ? 3000 : false),
  });
  const data = link.data;
  const justLinked = pending !== null && data?.linked;

  const connect = () => {
    setFailure("");
    // Вкладку открываем синхронно по клику, иначе браузер сочтёт её всплывающим окном.
    const tab = window.open("about:blank", "_blank");
    newTgLink()
      .then((got) => {
        setPending(got);
        if (tab) tab.location.href = got.url;
        else window.location.href = got.url;
      })
      .catch((error: Error) => {
        tab?.close();
        setFailure(error.message);
      });
  };

  return (
    <main
      className="grid min-h-screen place-items-center bg-bg px-4 py-8"
      data-annot="telegram"
    >
      <div className="flex w-full max-w-[420px] flex-col gap-4">
        <div className="flex items-center gap-2.5">
          <div className="grid size-6 place-items-center rounded-[5px] bg-fg text-[13px] font-semibold text-bg">
            Ю
          </div>
          <div className="text-[15px] font-semibold tracking-[-0.015em]">
            Юня · AI-ассистент компании
          </div>
        </div>
        <h1 className="text-[18px] font-semibold tracking-[-0.015em]">
          Подключить Telegram
        </h1>
        <p className="text-[13px] text-fg-muted">
          Бот @{data?.bot ?? "unirenteraibot"} принимает проблемы, баги и идеи
          текстом и голосом. Чтобы он знал, что это вы, нажмите кнопку, а в
          открывшемся Telegram — «Start».
        </p>

        {link.isPending ? (
          <div className="text-[13px] text-fg-muted">Загрузка…</div>
        ) : link.isError ? (
          <div className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
            {link.error.message}
          </div>
        ) : (
          <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[13px]">
            {data?.linked ? (
              <>
                <div className="font-medium text-fg">
                  {justLinked
                    ? "Готово, Telegram подключён"
                    : "Telegram подключён"}
                </div>
                <div className="mt-1 text-fg-muted">
                  Можно возвращаться в бота и писать.
                </div>
                <div className="mt-1 text-fg-muted">
                  {data.tg_username
                    ? `@${data.tg_username}`
                    : data.tg_name || "без имени"}{" "}
                  · с {mskTime(data.date_link)}
                </div>
              </>
            ) : waiting ? (
              <>
                <div className="font-medium text-fg">
                  Ждём «Start» в Telegram…
                </div>
                <div className="mt-1 text-fg-muted">
                  Не открылось?{" "}
                  <a
                    href={pending.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-fg underline"
                  >
                    Откройте ссылку
                  </a>{" "}
                  — она действует до {mskTime(pending.expire)}.
                </div>
              </>
            ) : (
              <div className="text-fg-muted">Telegram ещё не подключён.</div>
            )}
            <button
              type="button"
              onClick={connect}
              className="mt-3 h-9 rounded-[var(--radius-ui)] bg-fg px-4 text-[13.5px] font-medium text-bg"
            >
              {data?.linked ? "Подключить заново" : "Подключить Telegram"}
            </button>
          </div>
        )}

        {failure && (
          <div className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
            {failure}
          </div>
        )}
      </div>
    </main>
  );
}
